<script lang="ts" setup>import { computed as _computed } from 'vue';

import {
  faCalendar, faClock, faEnvelope, faMailboxFlagUp,
  faMessageLines,
  faPrint,
  faReplyTime,
} from '@fortawesome/pro-light-svg-icons'
import { formatDate, formatDeliveryDate, isBeforeNow } from '@consumer/utils/date'
import type { PurchasedOrderGift } from '@consumer/types/serializers'
import { formatPhoneNumber } from '@consumer/utils/phoneNumber'
import { speedbumpText } from '@consumer/constants/disclaimers'

const emit = defineEmits<{ giftNotDelivered: [] }>()

withDefaults(defineProps<{
  orderGift: PurchasedOrderGift
  wrapDateLine?: boolean
}>(), {  })

const delivery = _computed(() => __props.orderGift.delivery)
const deliveryMethod = _computed(() => delivery.value.deliveryMethod)
const mailingAddress = _computed(() => delivery.value.recipientMailingAddress)
const mailTrackingUrl = _computed(() => __props.orderGift.mailTrackingUrl)

const nudgeMethods = _computed(() => [
  deliveryMethod.value !== 'email' && delivery.value.recipientEmail,
  deliveryMethod.value !== 'text' && delivery.value.recipientPhoneNumber,
].filter(Boolean))

function openMailTrackingUrl () {
  if (!mailTrackingUrl.value) return
  // eslint-disable-next-line no-alert
  if (confirm(speedbumpText)) {
    window.open(mailTrackingUrl.value, '_blank', 'noopener')
  }
}
</script>

<template>
  <div>
    <div
      class="flex flex-col gap-2"
      :class="wrapDateLine ? 'lg:flex-col' : 'lg:flex-row lg:items-center'"
    >
      <div
        data-testid="order-gift-delivery-method"
        class="flex gap-2 items-center"
      >
        <template v-if="deliveryMethod === 'email'">
          <V2Icon :icon="faEnvelope" title="Email" class="stroke-v2-secondary"/>
          <div class="mt-0.5">
            {{ delivery.recipientEmail }}
          </div>
        </template>
        <template v-else-if="deliveryMethod === 'text'">
          <V2Icon :icon="faMessageLines" title="Text" class="stroke-v2-secondary"/>
          <div class="mt-0.5">
            {{ formatPhoneNumber(delivery.recipientPhoneNumber) }}
          </div>
        </template>
        <template v-else-if="deliveryMethod === 'mail'">
          <V2Icon :icon="faMailboxFlagUp" title="Mail" class="stroke-v2-secondary"/>
          <div v-if="mailingAddress" class="mt-0.5">
            {{ mailingAddress.street }}
            {{ mailingAddress.city }},
            {{ mailingAddress.state }}
            {{ mailingAddress.zip }}
          </div>
        </template>
        <template v-else>
          <V2Icon :icon="faPrint" title="Print" class="stroke-v2-secondary"/>
          <div class="mt-0.5">
            Printable PDF
          </div>
        </template>
      </div>

      <div :class="['hidden', wrapDateLine ? 'lg:hidden': 'lg:inline-block']">
        &bull;
      </div>

      <div data-testid="order-gift-delivery-date" class="flex gap-2 items-center">
        <V2Icon
          :icon="faCalendar"
          title="Calendar"
          class="stroke-v2-secondary"
          :class="wrapDateLine ? 'lg:inline-block' : 'lg:hidden'"
        />
        <div>
          <template v-if="orderGift.cancelledAt">
            Cancelled on {{ formatDate(orderGift.cancelledAt) }}
          </template>
          <template v-else-if="delivery.deliveryMethod === 'mail'">
            <template v-if="!delivery.deliveredAt || orderGift.statusLabel === 'Shipped'">
              Estimated delivery by {{ formatDate(delivery.estimatedDeliveryDate) }}
            </template>
            <template v-else>
              Sent to printer on {{ formatDate(delivery.deliveredAt) }}
            </template>
          </template>
          <template v-else-if="delivery.deliveredAt || orderGift.state === 'delivered'">
            <!-- deliveredAt may not be available immediately even after delivery ??? -->
            Sent on {{ formatDate(delivery.deliveredAt ?? new Date()) }}
          </template>
          <template
            v-else-if="isBeforeNow(delivery.deliverAt) ||
              !delivery.deliveryDate ||
              delivery.deliveryMethod === 'print'"
          >
            Scheduled for delivery ASAP
          </template>
          <template v-else>
            Scheduled for delivery <span class="no-screenshot">{{ formatDeliveryDate(delivery) }}</span>
          </template>
        </div>
      </div>

      <div
        v-if="deliveryMethod === 'mail' && mailTrackingUrl"
        class="text-v2-primary text-sm ml-6 lg:ml-0 mb-2 lg:mb-0"
      >
        <GButton
          flat
          name="track-mail-summary"
          label="Track"
          size="inherit"
          class="underline"
          @click.prevent="openMailTrackingUrl"
        />
      </div>

      <div
        v-if="deliveryMethod === 'mail' && orderGift.canRedeliverMailable"
        class="text-v2-primary text-sm ml-6 lg:ml-0 mb-2 lg:mb-0"
      >
        <GButton
          flat
          name="gift-not-delivered"
          label="Gift didn't arrive?"
          size="inherit"
          class="underline"
          @click.prevent="emit('giftNotDelivered')"
        />
      </div>

      <div
        v-if="deliveryMethod === 'mail' && orderGift.hasReplacementMailable"
        class="text-v2-secondary text-sm ml-6 lg:ml-0 mb-2 lg:mb-0"
      >
        (replacement)
      </div>
    </div>

    <div
      v-if="nudgeMethods.length"
      class="order-gift-nudge flex flex-col lg:flex-row gap-3 lg:gap-2 lg:items-center"
    >
      <div data-testid="order-gift-nudge-method" class="flex gap-2 items-center">
        <V2Icon :icon="faReplyTime" title="Nudged" class="stroke-v2-secondary"/>
        {{ nudgeMethods.join(' &bull; ') }}
      </div>
      <template v-if="orderGift.nudgedAt">
        <div class="hidden lg:inline-block">&bull;</div>
        <div data-testid="order-gift-nudge-date" class="flex gap-2 items-center">
          <V2Icon :icon="faClock" title="Nudged" class="stroke-v2-secondary lg:hidden"/>
          <div>
            <template v-if="orderGift.cancelledAt">
              Cancelled on {{ formatDate(orderGift.cancelledAt) }}
            </template>
            <template v-else>
              {{ orderGift.nudgedAt ? `Sent on ${formatDate(orderGift.nudgedAt)}` : '' }}
            </template>
          </div>
        </div>

      </template>
    </div>
  </div>
</template>
